import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { IqServiceForm } from '@/components/features/iq-services';
import { useCreateIqService } from '@/hooks';
import { ApiError } from '@/lib/api/client';
import type { CreateIqServiceRequest, UpdateIqServiceRequest } from '@/types/iq-service';

/**
 * IQ-Service create page.
 * Route: /iq-services/new (platform_admin only)
 */
export function IQServiceCreatePage() {
  const { t } = useTranslation('iq-services');
  const navigate = useNavigate();
  const createMutation = useCreateIqService();

  const handleSubmit = async (data: CreateIqServiceRequest | UpdateIqServiceRequest) => {
    try {
      const created = await createMutation.mutateAsync(data as CreateIqServiceRequest);
      toast.success(t('actions.createSuccess'));
      navigate(`/iq-services/${created.iqServiceId}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.error(t('actions.idempotencyConflict'));
        navigate('/iq-services');
      }
      // Other errors handled by form via createMutation.error
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('form.createTitle')}</h1>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/iq-services')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('detail.back')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('form.createTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <IqServiceForm
            mode="create"
            onSubmit={handleSubmit}
            onCancel={() => navigate('/iq-services')}
            isLoading={createMutation.isPending}
            error={createMutation.error}
          />
        </CardContent>
      </Card>
    </div>
  );
}
